import { Point2D, ShotRecommendation, PlayerPositionState, CourtZone } from './types';

/**
 * Calculates Court Zone for a given position
 * Y: 0 (top baseline), 50 (net), 100 (bottom baseline)
 */
export function getCourtZone(pos: Point2D): CourtZone {
  // Bottom player perspective
  if (pos.y > 90) {
    return 'defensive_red'; // Behind baseline
  } else if (pos.y >= 68 && pos.y <= 90) {
    return 'neutral_yellow'; // Near baseline to 3/4 court
  } else {
    return 'offensive_green'; // Inside service line / mid-court
  }
}

/**
 * Wardlaw Directionals Recommendation Engine
 * Analyzes current ball spot, incoming angle, player court position, and opponent position
 */
export function calculateShotRecommendations(state: PlayerPositionState): ShotRecommendation[] {
  const { ball, opponent1, incomingDirection, gameMode } = state;
  const userZone = getCourtZone(ball);
  const isRightSide = ball.x > 50; // Deuce side or Ad side
  const opponentIsLeft = opponent1.x < 50;

  const recommendations: ShotRecommendation[] = [];

  // 1. Primary Percentage Tennis Shot (High Success Safe Crosscourt)
  if (isRightSide) {
    // Ball on deuce side (right) -> Crosscourt to opponent deuce side (left from opponent view, x: 20-35, y: 15-25)
    recommendations.push({
      id: 'opt_crosscourt_drive',
      name: '重上旋深区大斜线 (高容错首选)',
      nameEn: 'Heavy Topspin Deep Crosscourt',
      targetPoint: { x: 25, y: 18 },
      strokeType: 'forehand_topspin',
      successRate: userZone === 'defensive_red' ? 82 : 90,
      riskLevel: 'low',
      netClearanceFeet: 3.8,
      speedMph: 65,
      tacticalPrinciple: '【沃德洛第一法则】顺向击球不改变球路。斜线对角线距离最长(82.5英尺)，球网中心最低(3英尺)，回球过网余量充足，迫使对手深位被动。',
      tacticalPrincipleEn: 'Wardlaw Rule #1: Take the ball where it came from. Crosscourt gives 4+ extra feet of baseline distance and passes the lowest part of the net.',
      targetZoneDesc: '对手反手深区后场底线压制角',
      drillNote: '击球点保持在身体前方，拍头从下往上充分刷球，球速可控但过网需留出3-4英尺弧线。',
      spinType: 'topspin',
      rpm: 3200,
      bounceHeightMeters: 1.65,
      magnusEffectDesc: '马格努斯向下强力俯冲：气压差压制球急剧下坠入界，触地后剧烈前冲高弹跳压迫肩部'
    });
  } else {
    // Ball on ad side (left) -> Crosscourt to opponent ad side (x: 75, y: 18)
    recommendations.push({
      id: 'opt_crosscourt_drive',
      name: '反手深区斜线稳固相持',
      nameEn: 'Solid Backhand Crosscourt Rally',
      targetPoint: { x: 75, y: 18 },
      strokeType: 'backhand_topspin',
      successRate: 85,
      riskLevel: 'low',
      netClearanceFeet: 3.5,
      speedMph: 62,
      tacticalPrinciple: '【相持基石】双反/单反顺向斜线压制对手正手深区。在红区与黄区绝不盲目变线，以深压浅，等待对手回球出浅。',
      tacticalPrincipleEn: 'Foundation Rally: Keep crosscourt rhythm, target the deep corner, wait for a short ball before attacking.',
      targetZoneDesc: '对手底线正手大角度深角',
      drillNote: '重心充分下沉，由蹬地带动髋部转动，顺势回位至中线偏左1米防守点。',
      spinType: 'topspin',
      rpm: 2800,
      bounceHeightMeters: 1.45,
      magnusEffectDesc: '稳定上旋下压：过网高度充裕，旋转保证安全回弹'
    });
  }

  // 2. Aggressive Down the Line or Change of Direction (Higher Risk, Decisive)
  if (userZone === 'offensive_green' || userZone === 'neutral_yellow') {
    const dtlTargetX = isRightSide ? 82 : 18;
    recommendations.push({
      id: 'opt_dtl_winner',
      name: isRightSide ? '正手穿透平击抽直线 (抓空档)' : '反手变线偷袭直线 (攻击网标)',
      nameEn: 'Down-the-Line Aggressive Drive',
      targetPoint: { x: dtlTargetX, y: 15 },
      strokeType: isRightSide ? 'forehand_flat' : 'backhand_topspin',
      successRate: userZone === 'offensive_green' ? 76 : 64,
      riskLevel: 'medium',
      netClearanceFeet: 1.8,
      speedMph: 75,
      tacticalPrinciple: '【沃德洛第二法则】改变球路需满足两条件：球在舒适击球区且落点在浅区。直线穿过边网最高处(3.5英尺)，球速需快且瞄准边线内侧1米安全区。',
      tacticalPrincipleEn: 'Wardlaw Rule #2: Only change the ball direction down the line when you step inside the court and take the ball in your strike zone.',
      targetZoneDesc: '空档半场底线边角',
      drillNote: '早侧身迎前，击球点击打在球的后中部偏外侧，随挥向前送出延伸。',
      spinType: 'flat',
      rpm: 1200,
      bounceHeightMeters: 1.05,
      magnusEffectDesc: '弱马格努斯力穿透弹道：极速平直，弹跳低快，穿透力极强但过网安全余量仅1-2英尺'
    });
  } else {
    // If defensive red zone: High Topspin Deep Reset
    recommendations.push({
      id: 'opt_defensive_moonball',
      name: '深位高弧度上旋重置球 (防守反击)',
      nameEn: 'Deep Topspin Moonball Reset',
      targetPoint: { x: 50, y: 12 },
      strokeType: 'topspin_lob',
      successRate: 88,
      riskLevel: 'low',
      netClearanceFeet: 5.5,
      speedMph: 55,
      tacticalPrinciple: '【红区解困心法】处于底线被动防守时，用充裕的过网高度争取回位时间，高弹跳将对手压退至底线后两米。',
      tacticalPrincipleEn: 'Red Zone Recovery: High arc, deep bounce buys you 2 seconds of recovery time.',
      targetZoneDesc: '对手底线中路深区 (限制其打出角度)',
      drillNote: '身体重心尽量保持稳定，拍面半开放向上刷过球心，制造深弹跳。',
      spinType: 'topspin',
      rpm: 3400,
      bounceHeightMeters: 1.85,
      magnusEffectDesc: '超高马格努斯上旋滞空：利用5英尺过网高度赢得回位时间，落地急坠并产生接近2米过头弹跳'
    });
  }

  // 3. Special Tactical Variation (Drop Shot or Inside-Out or Doubles Volley)
  if (gameMode === 'doubles') {
    recommendations.push({
      id: 'opt_doubles_feet',
      name: '追身压低对准网前截击者脚下 (双打核心)',
      nameEn: 'Low Dip at Net Player Feet',
      targetPoint: { x: 35, y: 42 },
      strokeType: 'forehand_topspin',
      successRate: 78,
      riskLevel: 'medium',
      netClearanceFeet: 1.5,
      speedMph: 60,
      tacticalPrinciple: '【双打黄金铁律】“打网前打脚下，打底线打中间”。把球压在网前选手的膝盖以下，迫使其向上抬球制造本方高压机会。',
      tacticalPrincipleEn: 'Doubles Cardinal Rule: Hit at the net player’s feet so they must pop the ball up.',
      targetZoneDesc: '对方网前选手移动落脚点',
      drillNote: '带强烈下沉上旋，拍头收缩，不过分追求球速，重点在弧线下沉弧度。',
      spinType: 'topspin',
      rpm: 3100,
      bounceHeightMeters: 0.7,
      magnusEffectDesc: '急剧下沉上旋弧线：越过球网后急速下扎到对手膝盖以下'
    });
  } else if (userZone === 'offensive_green') {
    recommendations.push({
      id: 'opt_touch_drop',
      name: '隐蔽轻切放短球 (克制深蹲退守者)',
      nameEn: 'Disguised Forehand/Backhand Drop Shot',
      targetPoint: { x: isRightSide ? 75 : 25, y: 44 },
      strokeType: 'forehand_drop',
      successRate: 70,
      riskLevel: 'high',
      netClearanceFeet: 1.0,
      speedMph: 35,
      tacticalPrinciple: '【出其不意】当对手退防至底线外2米准备吃深球时，引拍伪装为大抽球，触球瞬间手腕柔和吸收力量，利用球网遮蔽落入短角。',
      tacticalPrincipleEn: 'Deceptive Drop Shot: Disguise your backswing like a heavy drive, then soften wrist at contact.',
      targetZoneDesc: '近网小球死角 (发球区侧边)',
      drillNote: '切忌引拍减速过早暴露意图，利用下旋让球触地后向前滑动减少反弹。',
      spinType: 'slice',
      rpm: 2500,
      bounceHeightMeters: 0.35,
      magnusEffectDesc: '反向马格努斯下旋贴地滑行：触地后摩擦力迫使球向前滑动甚至向后倒旋，不弹起'
    });
  } else {
    recommendations.push({
      id: 'opt_inside_out',
      name: '侧身正手攻反手斜线 (Inside-Out)',
      nameEn: 'Inside-Out Forehand to Backhand Corner',
      targetPoint: { x: 22, y: 16 },
      strokeType: 'forehand_inside_out',
      successRate: 75,
      riskLevel: 'medium',
      netClearanceFeet: 2.8,
      speedMph: 72,
      tacticalPrinciple: '【正手主宰全场】利用侧身步绕过反手位，用统治级正手反斜线精准打击对手脆弱的反手弱翅，拉开全场空间。',
      tacticalPrincipleEn: 'Inside-Out Forehand: Use footwork to hit your stronger weapon into the opponent’s weaker backhand.',
      targetZoneDesc: '对手反手外角极限深区',
      drillNote: '脚步快速小碎步绕球，击球点在身体右前侧，拍头向上向外侧随挥。',
      spinType: 'topspin',
      rpm: 3300,
      bounceHeightMeters: 1.55,
      magnusEffectDesc: '侧上复合马格努斯旋转：球身在空中划出反C型外撇弧线，落地后向场外剧烈反弹'
    });
  }

  // Filter by spin if user specified a spinFilter
  if (state.spinFilter && state.spinFilter !== 'all') {
    const filtered = recommendations.filter((r) => r.spinType === state.spinFilter);
    if (filtered.length > 0) return filtered;
  }

  return recommendations;
}
